import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { getVulnerabiltyGraphDetails as getVulnerabilityGraphService } from 'services/modules/soc/agent';

import { Card, CardContent, Typography, Button, Box } from '@mui/material';
import Chart from 'react-apexcharts';
import CircularProgress from '@mui/material/CircularProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug } from '@fortawesome/free-solid-svg-icons';

function VulnerabilityDetectionGraph(props) {
  // React router
  const navigate = useNavigate();
  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData)
  // State variables
  const [isLoading, setIsLoading] = useState(true);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded',
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: [],
      title: {
        text: 'Vulnerability Published Year',
      },
    },
    yaxis: {
      title: {
        text: 'Severity Count',
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: val => `${val} vulnerabilities`,
      },
    },
    colors: ['#4e4ece', 'rgba(255, 165, 0, 0.92)', '#fc659c', 'rgba(255, 0, 0, 0.87)'],
  });
  const agentId = props.agentId;
  const endDate = props.endDate;
  const startDate = props.startDate;
  
  // Fetches vulnerability graph details based on agent id
  useEffect(() => {
    const fetchVulnerabilityData = async () => {
      try {
        setIsLoading(true);
        if (selectedCompanyContainer?.soc_env && endDate && startDate) {
          const response = await getVulnerabilityGraphService({ agentId: agentId, soc_env: selectedCompanyContainer?.soc_env, end: endDate, start: startDate });
          const buckets = response?.data?.rawResponse?.aggregations?.['2']?.buckets || [];
          const categories = [];
          const lowCounts = [];
          const mediumCounts = [];
          const highCounts = [];
          const criticalCounts = [];

          buckets.forEach(bucket => {
            const year = bucket?.key_as_string?.split('-')[0];
            if (!year) return;
            categories.push(year);

            const severityCounts = bucket['3']?.buckets?.reduce(
              (acc, item) => {
                acc[item.key] = item.doc_count || 0;
                return acc;
              },
              { Low: 0, Medium: 0, High: 0, Critical: 0 }
            );

            lowCounts.push(severityCounts.Low);
            mediumCounts.push(severityCounts.Medium);
            highCounts.push(severityCounts.High);
            criticalCounts.push(severityCounts.Critical);
          });

          setChartOptions(prevOptions => ({
            ...prevOptions,
            xaxis: {
              ...prevOptions.xaxis,
              categories,
            },
          }));

          setChartSeries([
            { name: 'Low', data: lowCounts },
            { name: 'Medium', data: mediumCounts },
            { name: 'High', data: highCounts },
            { name: 'Critical', data: criticalCounts },
          ]);
        }
      } catch (error) {
        setChartSeries([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVulnerabilityData();
  }, [agentId, selectedCompanyContainer?.soc_env, endDate, startDate]);

  const isDataEmpty = chartSeries.every(series => series.data.every(value => value === 0));

  return (
    <Card sx={{ margin: '10px', height: '450px' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <div className="table-cell-content">
            <FontAwesomeIcon icon={faBug} className="soc-icon" />
            <Typography variant="h6">Vulnerability Detection</Typography>
          </div>
          {!isDataEmpty && (
            <Button
              variant="contained"
              onClick={() =>
                navigate(`/soc/vulnerability-data/?agentname=${props.agentName}&agentid=${agentId}`, { state: { end: endDate, start: startDate } })
              }
            >
              View All
            </Button>
          )}
        </Box>
        <div style={{ position: 'relative', height: 350 }}>
          {isLoading ? (
            <div className="heatmap h-100 align-center">
              <CircularProgress />
            </div>
          ) : (
            <>
              <Chart options={chartOptions} series={chartSeries} type="bar" height={350} />
              {isDataEmpty && (
                <div
                 className='overlay'
                >
                  No Details Found
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default VulnerabilityDetectionGraph;
